import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Moon, Sun, Sparkles } from "lucide-react";

interface ZodiacSign {
  name: string;
  emoji: string;
  element: string;
  modality: string;
  ruler: string;
  traits: string[];
}

const zodiacSigns: ZodiacSign[] = [
  { name: "Aries", emoji: "♈", element: "Fuego", modality: "Cardinal", ruler: "Marte", traits: ["Pionero", "Valiente", "Impulsivo"] },
  { name: "Tauro", emoji: "♉", element: "Tierra", modality: "Fijo", ruler: "Venus", traits: ["Estable", "Sensual", "Persistente"] },
  { name: "Géminis", emoji: "♊", element: "Aire", modality: "Mutable", ruler: "Mercurio", traits: ["Comunicativo", "Versátil", "Curioso"] },
  { name: "Cáncer", emoji: "♋", element: "Agua", modality: "Cardinal", ruler: "Luna", traits: ["Protector", "Emocional", "Intuitivo"] },
  { name: "Leo", emoji: "♌", element: "Fuego", modality: "Fijo", ruler: "Sol", traits: ["Creativo", "Generoso", "Dramático"] },
  { name: "Virgo", emoji: "♍", element: "Tierra", modality: "Mutable", ruler: "Mercurio", traits: ["Analítico", "Servicial", "Perfeccionista"] },
  { name: "Libra", emoji: "♎", element: "Aire", modality: "Cardinal", ruler: "Venus", traits: ["Diplomático", "Armonioso", "Indeciso"] },
  { name: "Escorpio", emoji: "♏", element: "Agua", modality: "Fijo", ruler: "Plutón", traits: ["Intenso", "Transformador", "Misterioso"] },
  { name: "Sagitario", emoji: "♐", element: "Fuego", modality: "Mutable", ruler: "Júpiter", traits: ["Aventurero", "Optimista", "Filosófico"] },
  { name: "Capricornio", emoji: "♑", element: "Tierra", modality: "Cardinal", ruler: "Saturno", traits: ["Ambicioso", "Disciplinado", "Responsable"] },
  { name: "Acuario", emoji: "♒", element: "Aire", modality: "Fijo", ruler: "Urano", traits: ["Innovador", "Humanitario", "Independiente"] },
  { name: "Piscis", emoji: "♓", element: "Agua", modality: "Mutable", ruler: "Neptuno", traits: ["Compasivo", "Artístico", "Soñador"] },
];

interface NatalChartResult {
  sunSign: ZodiacSign;
  risingSign: ZodiacSign;
  moonSign: ZodiacSign;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
}

const NatalChartCalculator = () => {
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");
  const [birthPlace, setBirthPlace] = useState("");
  const [result, setResult] = useState<NatalChartResult | null>(null);
  const [error, setError] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const months = [
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
  ];

  const getSunSign = (day: number, month: number): ZodiacSign => {
    const dates = [
      { sign: 9, start: [1, 20] },   // Capricornio -> Acuario
      { sign: 10, start: [2, 19] },  // Acuario -> Piscis
      { sign: 11, start: [3, 21] },  // Piscis -> Aries
      { sign: 0, start: [4, 20] },   // Aries -> Tauro
      { sign: 1, start: [5, 21] },   // Tauro -> Géminis
      { sign: 2, start: [6, 21] },   // Géminis -> Cáncer
      { sign: 3, start: [7, 23] },   // Cáncer -> Leo
      { sign: 4, start: [8, 23] },   // Leo -> Virgo
      { sign: 5, start: [9, 23] },   // Virgo -> Libra
      { sign: 6, start: [10, 23] },  // Libra -> Escorpio
      { sign: 7, start: [11, 22] },  // Escorpio -> Sagitario
      { sign: 8, start: [12, 22] },  // Sagitario -> Capricornio
    ];

    for (let i = dates.length - 1; i >= 0; i--) {
      const { sign, start } = dates[i];
      if (month > start[0] || (month === start[0] && day >= start[1])) {
        return zodiacSigns[sign];
      }
    }
    return zodiacSigns[9]; // Capricornio
  };

  const getRisingSign = (hour: number): ZodiacSign => {
    // Simplified calculation: Rising sign changes approximately every 2 hours
    // This is a rough approximation - real calculation needs exact birth location
    const risingIndex = Math.floor(hour / 2) % 12;
    return zodiacSigns[risingIndex];
  };

  const getMoonSign = (day: number, month: number, year: number): ZodiacSign => {
    // Simplified moon sign calculation
    // Real calculation requires ephemeris data
    const totalDays = year * 365 + month * 30 + day;
    const lunarCycle = 27.3; // Moon changes sign approximately every 2.5 days
    const moonIndex = Math.floor((totalDays / lunarCycle) * 12) % 12;
    return zodiacSigns[moonIndex];
  };

  const calculateNatalChart = () => {
    setError("");
    setResult(null);

    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);

    // Validation
    if (!day || !month || !year || !hour || minute === "" || !birthPlace.trim()) {
      setError("Por favor, completa todos los campos");
      return;
    }

    if (yearNum < 1900 || yearNum > new Date().getFullYear()) {
      setError("Por favor, ingresa un año válido");
      return;
    }

    if (dayNum < 1 || dayNum > 31) {
      setError("Por favor, ingresa un día válido");
      return;
    }

    if (hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
      setError("Por favor, ingresa una hora válida");
      return;
    }

    const sunSign = getSunSign(dayNum, monthNum);
    const risingSign = getRisingSign(hourNum);
    const moonSign = getMoonSign(dayNum, monthNum, yearNum);

    setResult({
      sunSign,
      risingSign,
      moonSign,
      birthDate: `${dayNum}/${monthNum}/${yearNum}`,
      birthTime: `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`,
      birthPlace: birthPlace.trim(),
    });
  };

  const resetForm = () => {
    setDay("");
    setMonth("");
    setYear("");
    setHour("");
    setMinute("");
    setBirthPlace("");
    setResult(null);
    setError("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (!open) resetForm(); }}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-400/50 hover:border-indigo-400 hover:bg-indigo-500/30 text-foreground transition-all duration-300"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Calcular mi Carta Natal
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            Calculadora de Carta Natal
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Ingresa tu fecha, hora y lugar de nacimiento para descubrir tu carta natal con tu signo solar, lunar y ascendente.
            </p>

            {/* Birth Date */}
            <div className="space-y-2">
              <Label>Fecha de nacimiento</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Input
                    type="number"
                    placeholder="Día"
                    value={day}
                    onChange={(e) => setDay(e.target.value)}
                    min="1"
                    max="31"
                  />
                </div>
                <div>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger>
                      <SelectValue placeholder="Mes" />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder="Año"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                </div>
              </div>
            </div>

            {/* Birth Time */}
            <div className="space-y-2">
              <Label>Hora de nacimiento</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    type="number"
                    placeholder="Hora (0-23)"
                    value={hour}
                    onChange={(e) => setHour(e.target.value)}
                    min="0"
                    max="23"
                  />
                </div>
                <div>
                  <Input
                    type="number"
                    placeholder="Minutos (0-59)"
                    value={minute}
                    onChange={(e) => setMinute(e.target.value)}
                    min="0"
                    max="59"
                  />
                </div>
              </div>
            </div>

            {/* Birth Place */}
            <div className="space-y-2">
              <Label>Lugar de nacimiento</Label>
              <Input
                type="text"
                placeholder="Ej: Ciudad de México, México"
                value={birthPlace}
                onChange={(e) => setBirthPlace(e.target.value)}
                maxLength={100}
              />
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button onClick={calculateNatalChart} className="w-full">
              <Star className="w-4 h-4 mr-2" />
              Calcular Carta Natal
            </Button>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="text-center text-sm text-muted-foreground mb-4">
              <p>📅 {result.birthDate} • ⏰ {result.birthTime}</p>
              <p>📍 {result.birthPlace}</p>
            </div>

            {/* Sun Sign */}
            <Card className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-yellow-500/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{result.sunSign.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Sun className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Signo Solar</span>
                    </div>
                    <h3 className="text-lg font-bold">{result.sunSign.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {result.sunSign.element} • {result.sunSign.modality} • Regente: {result.sunSign.ruler}
                    </p>
                    <p className="text-sm mt-2">
                      Tu esencia y ego. Representa tu identidad central y cómo brillas en el mundo.
                    </p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {result.sunSign.traits.map((trait) => (
                        <span key={trait} className="text-xs bg-yellow-500/20 px-2 py-0.5 rounded-full">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Moon Sign */}
            <Card className="bg-gradient-to-br from-slate-500/10 to-blue-500/10 border-slate-400/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{result.moonSign.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Moon className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium text-slate-500 dark:text-slate-300">Signo Lunar</span>
                    </div>
                    <h3 className="text-lg font-bold">{result.moonSign.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {result.moonSign.element} • {result.moonSign.modality} • Regente: {result.moonSign.ruler}
                    </p>
                    <p className="text-sm mt-2">
                      Tu mundo emocional interno. Refleja tus instintos, reacciones y necesidades emocionales.
                    </p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {result.moonSign.traits.map((trait) => (
                        <span key={trait} className="text-xs bg-slate-500/20 px-2 py-0.5 rounded-full">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Rising Sign */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-400/30">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="text-3xl">{result.risingSign.emoji}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-purple-500 dark:text-purple-400">Ascendente</span>
                    </div>
                    <h3 className="text-lg font-bold">{result.risingSign.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {result.risingSign.element} • {result.risingSign.modality} • Regente: {result.risingSign.ruler}
                    </p>
                    <p className="text-sm mt-2">
                      Tu máscara social y primera impresión. Cómo te presentas al mundo y cómo te perciben los demás.
                    </p>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {result.risingSign.traits.map((trait) => (
                        <span key={trait} className="text-xs bg-purple-500/20 px-2 py-0.5 rounded-full">
                          {trait}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="text-xs text-muted-foreground text-center p-3 bg-muted/50 rounded-lg">
              <p className="font-medium mb-1">⚠️ Nota importante</p>
              <p>
                Esta es una aproximación educativa. Para una carta natal precisa, 
                se requieren cálculos astronómicos exactos basados en efemérides 
                y la ubicación geográfica exacta de nacimiento.
              </p>
            </div>

            <Button onClick={resetForm} variant="outline" className="w-full">
              Calcular otra carta natal
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NatalChartCalculator;
